import { SimpleMessageService, Tenant, WebAppCommonModule } from '@allbesmart/web-app-common';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { first, Subscription } from 'rxjs';
import { UserDto } from 'src/app/models/user/create-user-dto';
import { TenantManagementService } from 'src/app/services/tenant-management.service';
import { UserManagementService } from 'src/app/services/user-management.service';

@Component({
    selector: 'oaibox-user-create-page',
    templateUrl: './user-create-page.component.html',
    styleUrls: ['./user-create-page.component.css'],
    imports: [ReactiveFormsModule, WebAppCommonModule],
})
export class UserCreatePageComponent implements OnDestroy {

  protected newUserForm: NewUserFormGroup;

  private availableTenants: Tenant[] = [];
  protected searchedTenants: Tenant[] = [];
  protected selectedTenant?: Tenant;
  protected showTenantsList = false;

  protected ongoingRequest = false;

  private tenantsSubscription?: Subscription;
  private newUserSubscription?: Subscription;
  private userEmailFormSubscription?: Subscription;

  constructor(
    private userManagementService: UserManagementService,
    private tenantManagementService: TenantManagementService,
    private simpleMessageService: SimpleMessageService,
  ) {
    this.tenantsSubscription = this.tenantManagementService.getTenants$()
      .pipe(first())
      .subscribe(tenants => {
        this.availableTenants = tenants;
        this.searchedTenants = tenants;
      });

    this.tenantManagementService.refreshTenants();
    this.newUserForm = this.getCleanNewUserForm();
  }

  private getCleanNewUserForm(): NewUserFormGroup {
    const newUserForm: NewUserFormGroup = new FormGroup({
      keycloak: new FormGroup({
        id: new FormControl<string | null>(null, [Validators.required]),
        email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
        username: new FormControl<string | null>({value: null, disabled: true}, [Validators.required, Validators.email]),
      }),
      tenantSearch: new FormControl<string | null>(null),
    });

    this.userEmailFormSubscription?.unsubscribe();
    this.userEmailFormSubscription = newUserForm.controls.keycloak.controls.email.valueChanges
      .subscribe(value => {
        newUserForm.controls.keycloak.controls.username.setValue(value);
      });

    return newUserForm;
  }

  protected selectTenant(tenantId: string): void {
    this.selectedTenant = this.tenantManagementService.getTenantByTenantId(tenantId);
    this.showTenantsList = false;
  }

  protected filterSearchedTenants(keyword: string): void {
    const search = (keyword ?? '').trim().toLowerCase();

    this.searchedTenants = this.availableTenants.filter(tenant =>
      tenant.clientName.toLowerCase().includes(search) ||
      tenant.clientDescription.toLowerCase().includes(search) ||
      tenant.id.toLowerCase().includes(search)
    );
  }

  protected createUser(): void {
    if (this.newUserForm.invalid) return;

    const userDto: UserDto = {
      id: this.newUserForm.controls.keycloak.value.id ?? undefined,
      userEmail: this.newUserForm.controls.keycloak.value.email ?? undefined,
      username: this.newUserForm.controls.keycloak.getRawValue().username ?? undefined, // getRawValue() is a way to get value of a disabled control
      tenantId: this.selectedTenant?.id,
    };

    this.ongoingRequest = true;
    this.newUserSubscription = this.userManagementService.createUser(userDto).subscribe({
      next: () => {
        this.ongoingRequest = false;
        this.newUserForm = this.getCleanNewUserForm();
        this.simpleMessageService.showSimpleMessage({
          message: 'User created successfully!',
          icon: 'fa-solid fa-check-double',
          color: 'success',
        });
      },
      error: (err) => {
        this.ongoingRequest = false;
        this.simpleMessageService.showSimpleMessage({
          message: err.status === 409 ? 'User already exists' : 'User not created!',
          icon: 'fa-solid fa-triangle-exclamation',
          color: 'danger',
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.tenantsSubscription?.unsubscribe();
    this.newUserSubscription?.unsubscribe();
    this.userEmailFormSubscription?.unsubscribe();
  }

}

type NewUserFormGroup = FormGroup<{
  keycloak: FormGroup<{
    id: FormControl<string | null>;
    email: FormControl<string | null>;
    username: FormControl<string | null>;
  }>;
  tenantSearch: FormControl<string | null>;
}>;
